import { useEffect, useMemo, useState, type ReactNode } from "react"
import { ImagePlus, Save, Store } from "lucide-react"
import { useDispatch, useSelector } from "react-redux"
import toast from "react-hot-toast"
import StorefrontStoreIdCard from "../../components/Storefront/StorefrontStoreIdCard"
import {
  updateStore,
  uploadVehicleCoverImageToS3,
  uploadServiceShowcaseVideoToS3,
} from "../../api/products/post"
import { userData } from "../../store/actions"
import {
  normalizeServiceShowcaseItems,
  type ServiceShowcaseMediaItem,
} from "../../utils/serviceStorefront"
import { getStoreProductRules } from "../../utils/storeCategory"

type PendingFileMap = Record<string, File | null>
const MAX_SHOWCASE_VIDEO_BYTES = 2 * 1024 * 1024

export default function ServiceShowcasePage() {
  const dispatch = useDispatch()
  const userdata = useSelector((state: any) => state.UserReducer.userdata)
  const store = userdata?.store || {}
  const storeId = String(store?._id || store?.id || "")
  const { isServiceStore } = getStoreProductRules(store)

  const [items, setItems] = useState<ServiceShowcaseMediaItem[]>([])
  const [pendingImageFiles, setPendingImageFiles] = useState<PendingFileMap>({})
  const [pendingVideoFiles, setPendingVideoFiles] = useState<PendingFileMap>({})
  const [pendingBeforeImageFiles, setPendingBeforeImageFiles] = useState<PendingFileMap>({})
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState("")

  useEffect(() => {
    setItems(normalizeServiceShowcaseItems(store?.showcaseMediaItems))
    setPendingImageFiles({})
    setPendingVideoFiles({})
    setPendingBeforeImageFiles({})
    setError("")
  }, [store?.showcaseMediaItems])

  const previewUrls = useMemo(() => {
    return items.reduce<Record<string, string>>((acc, item) => {
      const imageFile = pendingImageFiles[item.id]
      const videoFile = pendingVideoFiles[item.id]
      if (imageFile) {
        acc[item.id] = URL.createObjectURL(imageFile)
      } else if (videoFile) {
        acc[item.id] = URL.createObjectURL(videoFile)
      } else {
        acc[item.id] = item.type === "VIDEO" ? item.videoUrl || item.thumbnailUrl : item.imageUrl
      }
      return acc
    }, {})
  }, [items, pendingImageFiles, pendingVideoFiles])

  const beforePreviewUrls = useMemo(() => {
    return items.reduce<Record<string, string>>((acc, item) => {
      const beforeImageFile = pendingBeforeImageFiles[item.id]
      if (beforeImageFile) {
        acc[item.id] = URL.createObjectURL(beforeImageFile)
      } else if (item.beforeImageUrl) {
        acc[item.id] = item.beforeImageUrl
      }
      return acc
    }, {})
  }, [items, pendingBeforeImageFiles])

  useEffect(() => {
    return () => {
      Object.values(previewUrls).forEach((value) => {
        if (value?.startsWith?.("blob:")) {
          URL.revokeObjectURL(value)
        }
      })
      Object.values(beforePreviewUrls).forEach((value) => {
        if (value?.startsWith?.("blob:")) {
          URL.revokeObjectURL(value)
        }
      })
    }
  }, [previewUrls, beforePreviewUrls])

  const updateItem = (
    index: number,
    field: keyof ServiceShowcaseMediaItem,
    value: string
  ) => {
    setItems((current) =>
      current.map((item, itemIndex) =>
        itemIndex === index ? { ...item, [field]: value } : item
      )
    )
  }

  const handleImageFileChange = (itemId: string, file: File | null) => {
    setPendingImageFiles((current) => ({ ...current, [itemId]: file }))
  }

  const handleBeforeImageFileChange = (itemId: string, file: File | null) => {
    setPendingBeforeImageFiles((current) => ({ ...current, [itemId]: file }))
  }

  const handleVideoFileChange = (itemId: string, file: File | null) => {
    if (file && Number(file.size || 0) > MAX_SHOWCASE_VIDEO_BYTES) {
      setError("La video de 'Nos realisations' doit faire 2 MB maximum.")
      return
    }

    setError("")
    setPendingVideoFiles((current) => ({ ...current, [itemId]: file }))
  }

  const handleSave = async () => {
    if (!storeId) return

    try {
      setSaving(true)
      setError("")

      const nextItems = await Promise.all(
        items.map(async (item) => {
          const imageFile = pendingImageFiles[item.id]
          const videoFile = pendingVideoFiles[item.id]
          const beforeImageFile = pendingBeforeImageFiles[item.id]
          const uploadedBefore = beforeImageFile
            ? await uploadVehicleCoverImageToS3(beforeImageFile, storeId)
            : null
          const beforeUrl = uploadedBefore?.imageUrl || uploadedBefore?.coverImageUrl || ""

          if (!imageFile && !videoFile) {
            return {
              ...item,
              title: item.title.trim(),
              description: item.description.trim(),
              badge: item.badge?.trim(),
              area: item.area?.trim(),
              beforeImageUrl: beforeUrl || item.beforeImageUrl || "",
              beforeImageKey:
                uploadedBefore?.coverImageKey || item.beforeImageKey || "",
            }
          }

          if (item.type === "VIDEO") {
            const uploadedVideo = videoFile
              ? await uploadServiceShowcaseVideoToS3(videoFile, storeId)
              : null
            const uploadedThumbnail = imageFile
              ? await uploadVehicleCoverImageToS3(imageFile, storeId)
              : null

            return {
              ...item,
              title: item.title.trim(),
              description: item.description.trim(),
              badge: item.badge?.trim(),
              area: item.area?.trim(),
              thumbnailUrl: uploadedThumbnail?.imageUrl || uploadedThumbnail?.coverImageUrl || item.thumbnailUrl,
              thumbnailKey: uploadedThumbnail?.coverImageKey || item.thumbnailKey,
              videoUrl: uploadedVideo?.videoUrl || item.videoUrl,
              videoKey: uploadedVideo?.videoKey || item.videoKey,
              beforeImageUrl: beforeUrl || item.beforeImageUrl || "",
              beforeImageKey:
                uploadedBefore?.coverImageKey || item.beforeImageKey || "",
            }
          }

          if (!imageFile) {
            return {
              ...item,
              title: item.title.trim(),
              description: item.description.trim(),
              badge: item.badge?.trim(),
              area: item.area?.trim(),
              beforeImageUrl: beforeUrl || item.beforeImageUrl || "",
              beforeImageKey:
                uploadedBefore?.coverImageKey || item.beforeImageKey || "",
            }
          }

          const uploaded = await uploadVehicleCoverImageToS3(imageFile, storeId)
          const uploadedUrl = uploaded?.imageUrl || uploaded?.coverImageUrl || ""

          return {
            ...item,
            title: item.title.trim(),
            description: item.description.trim(),
            badge: item.badge?.trim(),
            area: item.area?.trim(),
            imageUrl: uploadedUrl,
            imageKey: uploaded?.coverImageKey || item.imageKey,
            beforeImageUrl: beforeUrl || item.beforeImageUrl || "",
            beforeImageKey:
              uploadedBefore?.coverImageKey || item.beforeImageKey || "",
          }
        })
      )

      await updateStore(storeId, {
        showcaseMediaItems: nextItems,
      })

      dispatch(userData())
      setPendingImageFiles({})
      setPendingVideoFiles({})
      setPendingBeforeImageFiles({})
      toast.success("Nos realisations mises a jour")
    } catch (e: any) {
      setError(e?.message || "Impossible d'enregistrer les realisations")
    } finally {
      setSaving(false)
    }
  }

  if (!isServiceStore) {
    return <ServiceStorefrontUnavailable title="Nos realisations" />
  }

  return (
    <div className="space-y-6">
      <header className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex items-start gap-4">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-cyan-50 text-cyan-700">
            <ImagePlus size={22} />
          </div>
          <div>
            <p className="text-sm font-semibold uppercase tracking-[0.18em] text-cyan-700">Storefront</p>
            <h1 className="mt-2 text-2xl font-bold text-slate-900">Nos realisations</h1>
            <p className="mt-2 max-w-3xl text-sm leading-relaxed text-slate-600">
              Ajoutez les photos et videos de la vitrine service depuis Gestock. Les fichiers sont selectionnes depuis votre appareil puis enregistres sur la boutique active.
            </p>
          </div>
        </div>
      </header>

      <StorefrontStoreIdCard store={store} />

      {error ? <div className="rounded-2xl bg-red-50 p-4 text-sm text-red-600">{error}</div> : null}

      <div className="grid gap-5 lg:grid-cols-2">
        {items.map((item, index) => (
          <article key={item.id} className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-xs uppercase tracking-[0.16em] text-slate-400">Carte {index + 1}</p>
                <p className="mt-1 font-semibold text-slate-800">{item.type === "VIDEO" ? "Video" : "Photo"}</p>
              </div>
              <select
                value={item.type}
                onChange={(e) => updateItem(index, "type", e.target.value)}
                className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700"
              >
                <option value="PHOTO">Photo</option>
                <option value="VIDEO">Video</option>
              </select>
            </div>

            <div className="mt-4 space-y-4">
              <Field label="Titre">
                <input
                  value={item.title}
                  onChange={(e) => updateItem(index, "title", e.target.value)}
                  className="mt-1.5 w-full rounded-2xl border border-slate-200 px-4 py-3 text-slate-800"
                />
              </Field>

              <Field label="Description">
                <textarea
                  value={item.description}
                  onChange={(e) => updateItem(index, "description", e.target.value)}
                  rows={3}
                  className="mt-1.5 w-full rounded-2xl border border-slate-200 px-4 py-3 text-slate-800"
                />
              </Field>

              <div className="grid gap-4 md:grid-cols-2">
                <Field label="Badge (ex: Avant / apres)">
                  <input
                    value={item.badge || ""}
                    onChange={(e) => updateItem(index, "badge", e.target.value)}
                    placeholder="Avant / apres"
                    className="mt-1.5 w-full rounded-2xl border border-slate-200 px-4 py-3 text-slate-800"
                  />
                </Field>
                <Field label="Zone / secteur">
                  <input
                    value={item.area || ""}
                    onChange={(e) => updateItem(index, "area", e.target.value)}
                    placeholder="Sevrier, Annecy..."
                    className="mt-1.5 w-full rounded-2xl border border-slate-200 px-4 py-3 text-slate-800"
                  />
                </Field>
              </div>

              <Field label={item.type === "VIDEO" ? "Miniature optionnelle" : "Photo apres"}>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleImageFileChange(item.id, e.target.files?.[0] || null)}
                  className="mt-1.5 block w-full text-sm text-slate-600"
                />
              </Field>

              <Field label="Photo avant (optionnel - pour Avant/Apres)">
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleBeforeImageFileChange(item.id, e.target.files?.[0] || null)}
                  className="mt-1.5 block w-full text-sm text-slate-600"
                />
                {beforePreviewUrls[item.id] ? (
                  <img
                    src={beforePreviewUrls[item.id]}
                    alt="Avant"
                    className="mt-3 h-32 w-full rounded-2xl border border-slate-200 object-cover"
                  />
                ) : null}
              </Field>

              {item.type === "VIDEO" ? (
                <Field label="Video">
                  <input
                    type="file"
                    accept="video/*"
                    onChange={(e) => handleVideoFileChange(item.id, e.target.files?.[0] || null)}
                    className="mt-1.5 block w-full text-sm text-slate-600"
                  />
                  <p className="mt-1 text-xs text-slate-500">
                    Taille maximale: 2 MB.
                  </p>
                </Field>
              ) : null}

              <div className="overflow-hidden rounded-2xl border border-dashed border-slate-200 bg-slate-50">
                {item.type === "VIDEO" ? (
                  previewUrls[item.id] ? (
                    <video
                      src={previewUrls[item.id]}
                      className="h-56 w-full bg-black object-contain"
                      controls
                    />
                  ) : (
                    <div className="flex h-56 items-center justify-center text-sm text-slate-400">
                      Aucune video selectionnee
                    </div>
                  )
                ) : previewUrls[item.id] && beforePreviewUrls[item.id] ? (
                  <div className="grid h-56 grid-cols-2 gap-px bg-slate-200">
                    <div className="relative flex items-center justify-center bg-white">
                      <img
                        src={beforePreviewUrls[item.id]}
                        alt="Avant"
                        className="h-full w-full object-cover"
                      />
                      <span className="absolute left-2 top-2 rounded-full bg-slate-900/80 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-white">
                        Avant
                      </span>
                    </div>
                    <div className="relative flex items-center justify-center bg-white">
                      <img
                        src={previewUrls[item.id]}
                        alt={item.title}
                        className="h-full w-full object-cover"
                      />
                      <span className="absolute left-2 top-2 rounded-full bg-emerald-600/90 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-white">
                        Apres
                      </span>
                    </div>
                  </div>
                ) : previewUrls[item.id] ? (
                  <img
                    src={previewUrls[item.id]}
                    alt={item.title}
                    className="h-56 w-full object-cover"
                  />
                ) : (
                  <div className="flex h-56 items-center justify-center text-sm text-slate-400">
                    Aucune image selectionnee
                  </div>
                )}
              </div>
            </div>
          </article>
        ))}
      </div>

      <div className="flex justify-end">
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          className="inline-flex items-center gap-2 rounded-2xl bg-cyan-600 px-5 py-3 font-semibold text-white transition hover:bg-cyan-700 disabled:opacity-60"
        >
          <Save size={18} />
          {saving ? "Enregistrement..." : "Enregistrer les realisations"}
        </button>
      </div>
    </div>
  )
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="block">
      <span className="text-sm font-medium text-slate-700">{label}</span>
      {children}
    </label>
  )
}

function ServiceStorefrontUnavailable({ title }: { title: string }) {
  return (
    <div className="rounded-3xl border border-slate-200 bg-white p-8 shadow-sm">
      <div className="flex items-start gap-4">
        <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-100 text-slate-600">
          <Store size={22} />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-slate-900">{title}</h1>
          <p className="mt-2 text-sm text-slate-600">
            Cette page est reservee aux boutiques de type service.
          </p>
        </div>
      </div>
    </div>
  )
}
